import { useEffect, useMemo, useState } from 'react'
import type {
  QuickBooksActivePlan,
  QuickBooksActivePlanVariancePeriod,
  QuickBooksActivePlanVarianceResponse,
  ServerApiClient,
} from '../../../data/ServerApiClient'

const amount = (value: string | null | undefined, currencyCode: string): string => {
  if (value == null || value === '') return '—'
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return '—'
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 0,
    }).format(parsed)
  } catch {
    return `${currencyCode} ${parsed.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
  }
}

const percent = (value: string | null | undefined): string => {
  if (value == null || value === '') return '—'
  const parsed = Number(value)
  return Number.isFinite(parsed) ? `${(parsed * 100).toFixed(1)}%` : '—'
}

const periodLabel = (period: string): string => {
  const date = new Date(`${period.slice(0, 10)}T00:00:00Z`)
  return Number.isNaN(date.getTime())
    ? period
    : date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
}

export function ActivePlanVariancePanel({
  appApi,
  workbookId,
}: {
  appApi: ServerApiClient
  workbookId: string
}) {
  const [report, setReport] = useState<QuickBooksActivePlanVarianceResponse | null>(null)
  const [currentPlan, setCurrentPlan] = useState<QuickBooksActivePlan | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [switchingSource, setSwitchingSource] = useState(false)
  const [reloadNonce, setReloadNonce] = useState(0)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    const loadVariance = (appApi.getCfoActivePlanVariance || appApi.getQuickBooksActivePlanVariance)?.bind(appApi)
    const loadPlan = (appApi.getCfoActivePlan || appApi.getQuickBooksActivePlan)?.bind(appApi)
    if (typeof loadVariance !== 'function' || typeof loadPlan !== 'function') {
      setLoading(false)
      setError('The active-plan reporting contract is not available in this client session.')
      return () => { active = false }
    }
    Promise.all([loadVariance(workbookId), loadPlan(workbookId)])
      .then(([variance, plan]) => {
        if (!active) return
        setReport(variance)
        setCurrentPlan(plan)
      })
      .catch((cause) => { if (active) setError(cause instanceof Error ? cause.message : String(cause)) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [appApi, workbookId, reloadNonce])

  const selectedPeriod = useMemo(() => {
    const periods = report?.rows.flatMap((row) => row.periods) || []
    return periods
      .filter((period) => period.actual != null)
      .sort((left, right) => left.period_start.localeCompare(right.period_start))
      .at(-1)?.period_start || null
  }, [report])

  const rows = useMemo(() => {
    if (!report || !selectedPeriod) return []
    const candidates = report.rows.filter((row) => row.is_total && row.level <= 2)
    const displayRows = candidates.length > 0 ? candidates : report.rows
    return displayRows
      .map((row) => ({
        ...row,
        period: row.periods.find((period) => period.period_start === selectedPeriod) || null,
      }))
      .filter((row): row is typeof row & { period: QuickBooksActivePlanVariancePeriod } => Boolean(row.period))
      .slice(0, 12)
  }, [report, selectedPeriod])

  if (loading) return <article className="cfo-shell-notice"><strong>Loading active Plan vs Actual…</strong></article>
  if (error) return <article className="cfo-shell-notice"><strong>Active Plan vs Actual is unavailable</strong><p>{error}</p></article>
  if (!report || !selectedPeriod) return <article className="cfo-shell-notice"><strong>No comparable active-plan period is prepared yet.</strong></article>

  const integrity = report.coverage.actuals_integrity || 'unknown'
  const source = report.plan.primary_source || 'unknown'
  const currencyCode = report.currency_code
  const webCandidate = currentPlan?.web_candidates?.[0]
  const activateWebPlan = async () => {
    if (!webCandidate || !currentPlan?.publication_token || switchingSource) return
    if (!window.confirm(
      `Use “${webCandidate.name}” as the active plan? The workbook values remain in Excel, but Plan vs Actual will use the selected FuseGrid plan.`,
    )) return
    const activate = appApi.activateQuickBooksWebPlan?.bind(appApi)
    if (typeof activate !== 'function') {
      setError('This client cannot switch the active plan source.')
      return
    }
    setSwitchingSource(true)
    setError('')
    try {
      await activate(workbookId, {
        expected_publication_token: currentPlan.publication_token,
        plan_id: webCandidate.plan_id,
        confirmed_excel_supersession: source === 'excel',
        idempotency_key: `web-plan-activation:${crypto.randomUUID()}`,
      })
      setReloadNonce((value) => value + 1)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setSwitchingSource(false)
    }
  }
  return (
    <section style={{ marginBottom: 'var(--s5)' }} aria-label="Active Plan vs Actual">
      <div className="section-title rise">
        <h2 className="ff-guide">Active Plan vs Actual</h2>
        <span className="meta">{periodLabel(selectedPeriod)} · {source === 'excel' ? 'Excel-primary plan' : source.replaceAll('_', ' ')}</span>
      </div>
      <article className="cfo-shell-notice" style={{ marginBottom: 'var(--s3)' }}>
        <strong>{source === 'excel' ? 'The workbook is the active forecast source.' : 'FuseGrid is the active forecast source.'}</strong>
        <p style={{ margin: '6px 0 0', color: 'var(--muted)' }}>
          Actuals integrity is {integrity}. Closed periods use governed actuals; the current month is explicitly partial, and future periods never present an actual as zero.
        </p>
        {source === 'excel' && webCandidate ? (
          <button type="button" className="btn ghost" style={{ marginTop: 'var(--s3)' }} disabled={switchingSource} onClick={() => void activateWebPlan()}>
            {switchingSource ? 'Switching plan source…' : `Use FuseGrid plan: ${webCandidate.name}`}
          </button>
        ) : null}
      </article>
      <div className="dtable-wrap">
        <table className="dtable">
          <thead><tr><th>Line</th><th>Actual</th><th>Plan</th><th>Variance</th><th>Variance %</th><th>Result</th></tr></thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.statement_type}:${row.node_id}`}>
                <td>{row.label}</td>
                <td>{amount(row.period.actual, currencyCode)}</td>
                <td>{amount(row.period.plan, currencyCode)}</td>
                <td>{amount(row.period.variance, currencyCode)}</td>
                <td>{percent(row.period.variance_pct)}</td>
                <td>{row.period.favorability ? row.period.favorability.replaceAll('_', ' ') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export default ActivePlanVariancePanel
